import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CalendarClock,
  Check,
  CircleAlert,
  Keyboard,
  Lock,
  LogOut,
  PartyPopper,
  QrCode,
  Users,
  X,
} from 'lucide-react'
import QrScanner from 'qr-scanner'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TimeWindow } from '@sdg/shared'
import { Badge, Card, CardContent, Note } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { Field, Input } from '../components/ui/input'
import { cn } from '../lib/cn'
import { ApiError, get, post } from '../lib/api'
import { isWindowRefusal } from '../components/RegistrationGate'
import { formatDuration, formatMoment, splitDuration, useWindowState } from '../lib/countdown'
import { clearStaffSession, getStaffProfile, getStaffSession } from '../lib/staff'

interface BoothInfo {
  boothNo: number
  school: string
  visitorCount: number
  manager: { username: string; name: string | null }
  /** When this booth may mark attendance — Admin's booth window. */
  window: TimeWindow
}

interface ScannedStudent {
  regNo: string | null
  /** Null for an attendee from another institution; regNo identifies them. */
  studentId: string | null
  name: string
  school: string
}

type ScanResult =
  | {
    status: 'marked'
    student: ScannedStudent
    visited: number
    total: number
    drawEligible: boolean
    completed: boolean
    justQualifiedForDraw: boolean
    boothVisitorCount: number
  }
  | { status: 'duplicate'; student: ScannedStudent; attendedAt: string; boothVisitorCount: number }
  | { status: 'invalid'; reason: 'not_found' | 'unverified' | 'foreign_code' }

interface RecentScan {
  name: string
  regNo: string | null
  studentId: string
  scannedAt: string
  method: 'qr' | 'manual'
}

/**
 * Booth Manager scanner.
 *
 * The camera is opened only while scanning and shut off the instant a code is
 * read — the same rhythm as a payments app. Holding it open all day would
 * drain a phone at a booth that sees a student every few minutes, and keeps a
 * video decode running for nothing.
 */
type Mode = 'idle' | 'scanning' | 'result'

export default function BoothScanner() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const profile = getStaffProfile()

  const [mode, setMode] = useState<Mode>('idle')
  const [result, setResult] = useState<ScanResult | null>(null)
  const [cameraError, setCameraError] = useState<'denied' | 'unavailable' | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  /** Stops the camera firing the same code dozens of times a second. */
  const handledRef = useRef(false)

  const [manualOpen, setManualOpen] = useState(false)
  const [manualCode, setManualCode] = useState('')
  const [manualError, setManualError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [confirmingSignOut, setConfirmingSignOut] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const hasSession = !!getStaffSession()

  useEffect(() => {
    if (!hasSession && !signingOut) navigate('/staff/login', { replace: true })
  }, [hasSession, signingOut, navigate])

  const booth = useQuery({
    queryKey: ['booth'],
    queryFn: () => get<BoothInfo>('/api/booth/me', 'staff'),
    enabled: hasSession,
    // Only while idle: no polling competing with the camera for the main thread.
    refetchInterval: mode === 'idle' ? 30_000 : false,
  })

  const recent = useQuery({
    queryKey: ['booth-recent'],
    queryFn: () => get<RecentScan[]>('/api/booth/recent', 'staff'),
    enabled: hasSession,
  })

  /*
   * The booth window, ticking once a second off the server's clock.
   *
   * It rides along on `/api/booth/me`, which already refetches every 30
   * seconds while idle — so opening time arriving, or closing time passing,
   * moves this page by itself with nothing for the manager to reload.
   */
  const scanWindow = useWindowState(booth.data?.window, booth.isPending)
  const canScan = scanWindow.status === 'open'

  /**
   * Closing time passing with the camera open.
   *
   * Without this the viewfinder would stay up and keep decoding until the
   * manager happened to look, and every scan it read would be refused. Shut
   * it the moment the window does, and the closed card below takes over.
   */
  useEffect(() => {
    if (!canScan && mode === 'scanning') setMode('idle')
    if (!canScan) setManualOpen(false)
  }, [canScan, mode])

  const finish = useCallback(
    (outcome: ScanResult) => {
      setResult(outcome)
      setMode('result')
      queryClient.invalidateQueries({ queryKey: ['booth'] })
      queryClient.invalidateQueries({ queryKey: ['booth-recent'] })

      // A short buzz confirms the scan without the manager having to read
      // the screen — useful with a queue waiting.
      if (outcome.status === 'marked') navigator.vibrate?.(60)
      else navigator.vibrate?.([40, 60, 40])
    },
    [queryClient],
  )

  const submitScan = useCallback(
    async (payload: { qr?: string; code?: string }) => {
      setSubmitting(true)
      try {
        finish(await post<ScanResult>('/api/booth/scan', payload, 'staff'))
      } catch (err) {
        const failure = err as ApiError

        if (failure.status === 401) {
          clearStaffSession()
          navigate('/staff/login', { replace: true })
          return
        }

        /*
         * The window shut while this page was open — a tab left up since the
         * morning, or a device clock a few minutes out.
         *
         * Deliberately *not* reported as a result: "No student found for that
         * code" would blame the student's pass for the Committee's clock, and
         * the manager would send a perfectly valid pass to the help desk. Any
         * scan we might show would be stale anyway. Re-read the booth instead,
         * which flips the page to the countdown or the closed card and says so
         * in as many words.
         */
        if (isWindowRefusal(failure.message)) {
          setMode('idle')
          setManualOpen(false)
          void queryClient.invalidateQueries({ queryKey: ['booth'] })
          return
        }

        finish({ status: 'invalid', reason: 'not_found' })
      } finally {
        setSubmitting(false)
      }
    },
    [finish, navigate, queryClient],
  )

  /**
   * The camera's whole lifetime is this effect. Entering `scanning` starts it;
   * leaving for any reason — a result, cancel, sign-out, unmount — tears it
   * down, so the indicator light goes out and the decode loop stops.
   */
  useEffect(() => {
    if (mode !== 'scanning' || !videoRef.current) return

    handledRef.current = false
    const scanner = new QrScanner(
      videoRef.current,
      (decoded) => {
        if (handledRef.current) return
        handledRef.current = true
        // Stop before the request: the answer replaces the viewfinder anyway.
        scanner.stop()
        void submitScan({ qr: decoded.data })
      },
      {
        // The rear camera is the one pointed at the student's phone.
        preferredCamera: 'environment',
        highlightScanRegion: false,
        highlightCodeOutline: false,
        maxScansPerSecond: 8,
      },
    )

    scanner
      .start()
      .then(() => setCameraError(null))
      .catch((err: unknown) => {
        const denied = (err as { name?: string })?.name === 'NotAllowedError'
        setCameraError(denied ? 'denied' : 'unavailable')
        setMode('idle')
        // A camera failure must never stop the queue (slide 11).
        setManualOpen(true)
      })

    return () => {
      scanner.stop()
      scanner.destroy()
    }
  }, [mode, submitScan])

  async function onManualSubmit(e: React.FormEvent) {
    e.preventDefault()
    const code = manualCode.trim()
    if (code.length < 3) {
      setManualError('Enter the registration number or Student ID.')
      return
    }
    setManualError(null)
    setManualOpen(false)
    setManualCode('')
    await submitScan({ code })
  }

  async function signOut() {
    if (signingOut) return
    setSigningOut(true)
    setMode('idle') // Shut the camera before leaving.
    await post('/api/staff/logout', {}, 'staff').catch(() => { })
    setConfirmingSignOut(false)
    clearStaffSession()
    navigate('/staff/login', { replace: true })
    queryClient.removeQueries({ queryKey: ['booth'] })
    queryClient.removeQueries({ queryKey: ['booth-recent'] })
  }

  const boothNo = booth.data?.boothNo ?? profile?.booth?.boothNo
  const school = booth.data?.school ?? profile?.booth?.school

  return (
    <>
      {/* Which booth am I marking for — checked at a glance all day. */}
      <Card className="mb-3 overflow-hidden p-0">
        <div className="flex items-center gap-3 bg-linear-to-br from-navy-600 to-navy-950 px-4 py-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-white/15 text-lg font-bold text-white tabular-nums">
            {boothNo ?? '—'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/55">
              Booth {boothNo ?? ''} · Scanner
            </p>
            <p className="truncate text-[13px] font-bold leading-tight text-white">
              {school ?? '…'}
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[12px] font-bold text-white tabular-nums">
            <Users className="size-3.5" strokeWidth={2.4} />
            {booth.data?.visitorCount ?? 0}
          </span>
        </div>
      </Card>

      {/*
        Outside the window the scan controls are replaced, not merely
        disabled. A greyed-out button with no explanation is the thing a
        manager takes to the help desk; a countdown answers the question
        before it is asked.
      */}
      {scanWindow.status === 'before' && (
        <ScanOpensSoon
          opensAt={booth.data?.window.opensAt ?? null}
          seconds={scanWindow.untilOpen ?? 0}
        />
      )}

      {scanWindow.status === 'closed' && (
        <ScanClosed closedAt={booth.data?.window.closesAt ?? null} />
      )}

      {canScan && mode === 'idle' && (
        <Card>
          <CardContent className="text-center">
            <span className="mx-auto mb-4 grid size-20 place-items-center rounded-full bg-teal-soft text-teal">
              <QrCode className="size-10" strokeWidth={1.8} />
            </span>
            <h1 className="text-[19px] font-bold text-navy-950">Ready to scan</h1>
            <p className="mx-auto mt-1.5 max-w-70 text-[13px] leading-relaxed text-muted">
              The camera opens only while you scan, then closes again — it will not run in
              the background.
            </p>

            {/*
              Closing time is near. Worth saying on the scanner, because the
              manager is the one who has to tell the queue in front of them.
            */}
            {scanWindow.enforced &&
              scanWindow.untilClose !== null &&
              scanWindow.untilClose > 0 &&
              scanWindow.untilClose <= 3600 && (
                <Note tone="amber" className="mt-4 text-left">
                  <span className="flex items-start gap-2.5">
                    <CalendarClock className="mt-0.5 size-4 shrink-0" strokeWidth={2.2} />
                    <span>
                      Booth visits close in{' '}
                      <b className="font-bold">{formatDuration(scanWindow.untilClose)}</b>, at{' '}
                      {formatMoment(booth.data?.window.closesAt ?? null)}.
                    </span>
                  </span>
                </Note>
              )}

            {cameraError && (
              <Note tone="error" className="mt-4 text-left">
                {cameraError === 'denied'
                  ? 'Camera permission was refused. Allow it in your browser settings, or keep entering codes by hand.'
                  : 'No camera available on this device. Enter codes by hand instead.'}
              </Note>
            )}

            <Button block size="lg" className="mt-5" onClick={() => setMode('scanning')}>
              <QrCode className="size-5" strokeWidth={2.2} />
              Scan student pass
            </Button>

            <Button variant="outline" block className="mt-2.5" onClick={() => setManualOpen(true)}>
              <Keyboard className="size-4" strokeWidth={2.2} />
              Enter code by hand
            </Button>
          </CardContent>
        </Card>
      )}

      {canScan && mode === 'scanning' && (
        <Card className="overflow-hidden p-0">
          <div className="relative aspect-square w-full bg-navy-950">
            <video
              ref={videoRef}
              className="size-full object-cover"
              playsInline
              muted
              aria-label="Booth camera"
            />

            <span className="pointer-events-none absolute left-6 top-6 size-11 rounded-tl-xl border-l-4 border-t-4 border-white/85" />
            <span className="pointer-events-none absolute right-6 top-6 size-11 rounded-tr-xl border-r-4 border-t-4 border-white/85" />
            <span className="pointer-events-none absolute bottom-6 left-6 size-11 rounded-bl-xl border-b-4 border-l-4 border-white/85" />
            <span className="pointer-events-none absolute bottom-6 right-6 size-11 rounded-br-xl border-b-4 border-r-4 border-white/85" />

            <p className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-[12.5px] font-bold text-white/85">
              {submitting ? 'Checking…' : 'Point at the student’s QR pass'}
            </p>
          </div>

          <CardContent className="space-y-2.5">
            <Button variant="outline" block onClick={() => setMode('idle')}>
              <X className="size-4" strokeWidth={2.4} />
              Cancel
            </Button>
            <Button
              variant="ghost"
              block
              onClick={() => {
                setMode('idle')
                setManualOpen(true)
              }}
            >
              <Keyboard className="size-4" strokeWidth={2.2} />
              Camera not reading it? Type the code
            </Button>
          </CardContent>
        </Card>
      )}

      {/*
        A result stays readable even if the window has just closed: the scan it
        describes already happened and was saved. Only the "Scan next student"
        button goes, because that one would be refused.
      */}
      {mode === 'result' && result && (
        <Card className="overflow-hidden p-0">
          <ResultPanel result={result} />
          <CardContent className="space-y-2.5">
            {canScan && (
              <Button
                block
                size="lg"
                onClick={() => {
                  setResult(null)
                  setMode('scanning')
                }}
              >
                <QrCode className="size-5" strokeWidth={2.2} />
                Scan next student
              </Button>
            )}
            <Button
              variant="outline"
              block
              onClick={() => {
                setResult(null)
                setMode('idle')
              }}
            >
              Done
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Reassurance that scans are landing. */}
      {mode !== 'scanning' && !!recent.data?.length && (
        <Card className="mt-3">
          <CardContent>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-muted">
              Just scanned
            </p>
            <ul className="space-y-1.5">
              {recent.data.slice(0, 5).map((r) => (
                <li
                  key={`${r.studentId}-${r.scannedAt}`}
                  className="flex items-center gap-2.5 text-[13px]"
                >
                  <Check className="size-3.5 shrink-0 text-green" strokeWidth={3} />
                  <span className="font-bold text-ink tabular-nums">
                    {r.regNo ?? r.studentId}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-muted">{r.name}</span>
                  <span className="shrink-0 text-[11px] text-muted-3">
                    {new Date(r.scannedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="mt-2 text-center">
        <Button
          variant="link"
          className="font-normal text-muted"
          onClick={() => setConfirmingSignOut(true)}
        >
          <LogOut className="size-3.5" strokeWidth={2.2} />
          Sign out of Booth {boothNo ?? ''}
        </Button>
      </div>

      {/* Manual fallback — required when a camera fails (slide 11). */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="p-0">
          <div className="px-6 pb-6 pt-7">
            <DialogTitle className="text-[17px]">Enter code by hand</DialogTitle>
            <DialogDescription className="mt-1.5">
              Use the registration number on the student’s pass (for example A012), or their
              full Student ID.
            </DialogDescription>

            <form onSubmit={onManualSubmit} className="mt-4">
              <Field
                label="Registration number or Student ID"
                htmlFor="m-code"
                error={manualError ?? undefined}
              >
                <Input
                  id="m-code"
                  value={manualCode}
                  onChange={(e) => {
                    setManualCode(e.target.value.toUpperCase())
                    setManualError(null)
                  }}
                  placeholder="A012"
                  autoCapitalize="characters"
                  spellCheck={false}
                  className="text-center text-lg font-bold tracking-widest tabular-nums"
                  autoFocus
                />
              </Field>
              <Button type="submit" block size="lg" className="mt-4" disabled={submitting}>
                {submitting ? 'Checking…' : 'Mark attendance'}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmingSignOut} onOpenChange={setConfirmingSignOut}>
        <DialogContent className="p-0" showClose={false}>
          <div className="px-6 pb-6 pt-7 text-center">
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-orange-soft text-orange">
              <LogOut className="size-5" strokeWidth={2.2} />
            </span>
            <DialogTitle className="text-[17px]">Sign out of Booth {boothNo ?? ''}?</DialogTitle>
            <DialogDescription className="mt-1.5">
              Every scan is already saved. You will need your username and password to start
              scanning again.
            </DialogDescription>
            <div className="mt-5 flex gap-2.5">
              <Button
                variant="outline"
                block
                disabled={signingOut}
                onClick={() => setConfirmingSignOut(false)}
                autoFocus
              >
                Keep scanning
              </Button>
              <Button variant="navy" block disabled={signingOut} onClick={signOut}>
                {signingOut ? 'Signing out…' : 'Sign out'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Before the booths open: a live countdown in place of the scan controls.
 *
 * Four tiles rather than a sentence, for the same reason the student page uses
 * them — a manager setting up at 08:20 wants to know *how long*, not to work
 * it out from a date. The seconds tile is what makes it visibly live; without
 * it the card reads as a static notice and gets reloaded.
 */
function ScanOpensSoon({ opensAt, seconds }: { opensAt: string | null; seconds: number }) {
  const parts = splitDuration(seconds)
  const tiles = [
    { label: parts.days === 1 ? 'Day' : 'Days', value: parts.days },
    { label: 'Hours', value: parts.hours },
    { label: 'Minutes', value: parts.minutes },
    { label: 'Seconds', value: parts.seconds },
  ]

  return (
    <Card>
      <CardContent className="text-center">
        {/* amber-ink, not amber: #fca311 on #fff4dc is too faint to read. */}
        <span className="mx-auto mb-4 grid size-20 place-items-center rounded-full bg-amber-soft text-amber-ink">
          <CalendarClock className="size-10" strokeWidth={1.8} />
        </span>
        <h1 className="text-[19px] font-bold text-navy-950">Booth visits not open yet</h1>
        <p className="mx-auto mt-1.5 max-w-72 text-[13px] leading-relaxed text-muted">
          You are signed in and this booth is ready. Scanning unlocks by itself when the
          countdown ends — you do not need to sign in again.
        </p>

        <div className="mt-5 grid grid-cols-4 gap-2" role="timer" aria-live="off">
          {tiles.map((tile) => (
            <div key={tile.label} className="rounded-xl bg-surface px-1 py-3">
              <p className="text-2xl font-bold leading-none text-navy-950 tabular-nums">
                {String(tile.value).padStart(2, '0')}
              </p>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-3">
                {tile.label}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-4 text-[12.5px] text-muted">
          Opens <b className="font-bold text-navy-950">{formatMoment(opensAt)}</b>
        </p>
      </CardContent>
    </Card>
  )
}

/** After the booths close. Scans already taken are kept and already counted. */
function ScanClosed({ closedAt }: { closedAt: string | null }) {
  return (
    <Card>
      <CardContent className="text-center">
        <span className="mx-auto mb-4 grid size-20 place-items-center rounded-full bg-orange-soft text-orange">
          <Lock className="size-9" strokeWidth={2} />
        </span>
        <h1 className="text-[19px] font-bold text-navy-950">Booth visits are closed</h1>
        <p className="mx-auto mt-1.5 max-w-72 text-[13px] leading-relaxed text-muted">
          Attendance can no longer be marked at this booth. Every scan you took today is
          saved and already counted towards the students’ progress.
        </p>

        <Note tone="amber" className="mt-4 text-left">
          <span className="flex items-start gap-2.5">
            <CalendarClock className="mt-0.5 size-4 shrink-0" strokeWidth={2.2} />
            <span>
              Closed at <b className="font-bold">{formatMoment(closedAt)}</b>.
            </span>
          </span>
        </Note>
      </CardContent>
    </Card>
  )
}

/**
 * The outcome, large enough to read at arm's length in a noisy hall. Slide 11
 * shows the student's name, ID and school so the manager can compare them
 * with the student card.
 */
function ResultPanel({ result }: { result: ScanResult }) {
  if (result.status === 'invalid') {
    const message =
      result.reason === 'foreign_code'
        ? 'That code is not an event pass.'
        : result.reason === 'unverified'
          ? 'That registration was never completed. Send them to the help desk.'
          : 'No student found for that code.'

    return (
      <Panel tone="orange" icon={<CircleAlert className="size-9" strokeWidth={2.4} />} title="Invalid pass">
        <p className="text-[13.5px] text-white/90">{message}</p>
      </Panel>
    )
  }

  if (result.status === 'duplicate') {
    return (
      <Panel tone="amber" icon={<CircleAlert className="size-9" strokeWidth={2.4} />} title="Already attended">
        <Identity student={result.student} tone="amber" />
        <p className="mt-2 text-[13.5px] font-bold text-navy-950/80">
          Marked here at{' '}
          {new Date(result.attendedAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
          . Not counted twice.
        </p>
      </Panel>
    )
  }

  return (
    <Panel
      tone="green"
      icon={
        result.completed || result.justQualifiedForDraw ? (
          <PartyPopper className="size-9" strokeWidth={2.4} />
        ) : (
          <Check className="size-10" strokeWidth={3} />
        )
      }
      title="Attendance marked"
    >
      <Identity student={result.student} tone="green" />
      <p className="mt-2.5 text-[17px] font-bold text-white tabular-nums">
        Progress {result.visited} / {result.total}
      </p>
      {result.completed ? (
        <p className="mt-1 text-[13px] font-bold text-white">
          All {result.total} booths done — certificate on its way.
        </p>
      ) : result.justQualifiedForDraw ? (
        <p className="mt-1 text-[13px] font-bold text-white">Now in the lucky draw.</p>
      ) : null}
    </Panel>
  )
}

function Identity({ student, tone }: { student: ScannedStudent; tone: 'green' | 'amber' }) {
  const strong = tone === 'amber' ? 'text-navy-950' : 'text-white'
  const soft = tone === 'amber' ? 'text-navy-950/75' : 'text-white/80'
  return (
    <>
      <p className={cn('text-xl font-bold leading-tight', strong)}>{student.name}</p>
      <p className={cn('mt-1 text-[13px]', soft)}>
        {/*
          A visitor from another institution has no Student ID, so the
          registration code stands alone rather than trailing a separator
          with nothing after it.
        */}
        {student.regNo && (
          <span className="font-bold tracking-wide tabular-nums">
            {student.regNo}
            {student.studentId ? ' · ' : ''}
          </span>
        )}
        {student.studentId}
      </p>
      <p className={cn('text-[12.5px]', soft)}>{student.school}</p>
    </>
  )
}

function Panel({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'green' | 'amber' | 'orange'
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  const bg = { green: 'bg-green', amber: 'bg-amber', orange: 'bg-orange' }[tone]
  const chip =
    tone === 'amber' ? 'bg-navy-950/15 text-navy-950' : 'bg-white/20 text-white'

  return (
    <div
      className={cn(
        'flex min-h-72 flex-col items-center justify-center px-6 py-8 text-center',
        'animate-overlay-in',
        bg,
      )}
    >
      <span className={cn('mb-3 grid size-18 place-items-center rounded-full', chip)}>{icon}</span>
      <Badge className={cn('mb-3', chip)}>{title}</Badge>
      {children}
    </div>
  )
}
